import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, ArrowLeft, Send, Users, CheckCircle, XCircle, Clock, AlertCircle, Loader2, Package, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import lettersApi, { LetterTemplate, BulkMailCampaign, LetterSend } from '@/services/lettersApi';
import { apiClient } from '@/services/apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type View = 'list' | 'create' | 'detail';

type TargetMode = 'group' | 'program' | 'manual';

interface GroupOption {
  id: number;
  name: string;
}

interface ProgramOption {
  id: number;
  name: string;
}

interface MergeField {
  key: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE_VARIANT: Record<BulkMailCampaign['status'], string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  queued: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
};

function StatusBadge({ status }: { status: BulkMailCampaign['status'] }) {
  return (
    <Badge variant="outline" className={STATUS_BADGE_VARIANT[status] ?? ''}>
      {status}
    </Badge>
  );
}

const SEND_STATUS_ICON: Record<string, React.ReactNode> = {
  created: <Clock className="h-4 w-4 text-gray-400" />,
  processing: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
  mailed: <Package className="h-4 w-4 text-blue-500" />,
  delivered: <CheckCircle className="h-4 w-4 text-green-500" />,
  returned: <RotateCw className="h-4 w-4 text-yellow-500" />,
  failed: <XCircle className="h-4 w-4 text-red-500" />,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BulkMailCampaignPage: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // List view state
  const [campaigns, setCampaigns] = useState<BulkMailCampaign[]>([]);

  // Detail view state
  const [selectedCampaign, setSelectedCampaign] = useState<BulkMailCampaign | null>(null);
  const [campaignSends, setCampaignSends] = useState<LetterSend[]>([]);
  const [sendsLoading, setSendsLoading] = useState(false);

  // Create view state
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);

  const [campaignName, setCampaignName] = useState('');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [targetMode, setTargetMode] = useState<TargetMode>('group');
  const [groupId, setGroupId] = useState<number | null>(null);
  const [programId, setProgramId] = useState<number | null>(null);
  const [manualMemberIds, setManualMemberIds] = useState('');
  const [mailType, setMailType] = useState<'usps_first_class' | 'usps_standard'>('usps_first_class');
  const [color, setColor] = useState(false);
  const [doubleSided, setDoubleSided] = useState(false);
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await lettersApi.listBulkCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCreateData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpls, grpResp, prgResp] = await Promise.all([
        lettersApi.listTemplates(),
        apiClient.get('/api/groups'),
        apiClient.get('/api/programs'),
      ]);
      setTemplates(tpls);
      setGroups(Array.isArray(grpResp.data) ? grpResp.data : []);
      setPrograms(Array.isArray(prgResp.data) ? prgResp.data : []);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load form data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampaignDetail = useCallback(async (id: number) => {
    setLoading(true);
    setSendsLoading(true);
    setError(null);
    try {
      const [campaign, sends] = await Promise.all([
        lettersApi.getBulkCampaign(id),
        lettersApi.getBulkCampaignSends(id),
      ]);
      setSelectedCampaign(campaign);
      setCampaignSends(sends);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load campaign details');
    } finally {
      setLoading(false);
      setSendsLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (view === 'list') {
      loadCampaigns();
    }
  }, [view, loadCampaigns]);

  useEffect(() => {
    if (view === 'create') {
      loadCreateData();
    }
  }, [view, loadCreateData]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setCampaignName('');
    setTemplateId(null);
    setTargetMode('group');
    setGroupId(null);
    setProgramId(null);
    setManualMemberIds('');
    setMailType('usps_first_class');
    setColor(false);
    setDoubleSided(false);
    setMergeFields([]);
    setError(null);
    setView('create');
  };

  const openDetail = (campaign: BulkMailCampaign) => {
    setSelectedCampaign(campaign);
    loadCampaignDetail(campaign.id);
    setView('detail');
  };

  const goBack = () => {
    setSelectedCampaign(null);
    setCampaignSends([]);
    setError(null);
    setView('list');
  };

  const handleCancel = async (id: number) => {
    try {
      await lettersApi.cancelBulkCampaign(id);
      loadCampaigns();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to cancel campaign');
    }
  };

  const handleSubmit = async () => {
    if (!campaignName.trim() || !templateId) {
      setError('Campaign name and template are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const mergeDataDefaults: Record<string, string> = {};
      mergeFields.forEach((f) => {
        if (f.key.trim()) mergeDataDefaults[f.key.trim()] = f.value;
      });

      const payload: any = {
        name: campaignName.trim(),
        template_id: templateId,
        mail_type: mailType,
        color,
        double_sided: doubleSided,
        merge_data_defaults: Object.keys(mergeDataDefaults).length ? mergeDataDefaults : undefined,
      };

      if (targetMode === 'group' && groupId) {
        payload.group_id = groupId;
      } else if (targetMode === 'program' && programId) {
        payload.program_id = programId;
      } else if (targetMode === 'manual' && manualMemberIds.trim()) {
        payload.member_ids = manualMemberIds
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !isNaN(n));
      }

      await lettersApi.createBulkCampaign(payload);
      setView('list');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const addMergeField = () => setMergeFields([...mergeFields, { key: '', value: '' }]);
  const removeMergeField = (idx: number) => setMergeFields(mergeFields.filter((_, i) => i !== idx));
  const updateMergeField = (idx: number, field: 'key' | 'value', val: string) => {
    const updated = [...mergeFields];
    updated[idx] = { ...updated[idx], [field]: val };
    setMergeFields(updated);
  };

  // ---------------------------------------------------------------------------
  // Progress helpers
  // ---------------------------------------------------------------------------

  const progressPct = (c: BulkMailCampaign) => {
    if (!c.total_count) return 0;
    return Math.round(((c.sent_count + c.delivered_count + c.returned_count + c.failed_count) / c.total_count) * 100);
  };

  const fmtDate = (d?: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ---------------------------------------------------------------------------
  // RENDER: List View
  // ---------------------------------------------------------------------------

  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold">Bulk Mail Campaigns</h1>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Campaign
          </Button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No bulk mail campaigns yet</p>
              <p className="text-sm mt-1">Create your first campaign to send physical mail to your members.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Returned</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openDetail(c)}
                  >
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-right">{c.total_count}</TableCell>
                    <TableCell className="text-right">{c.sent_count}</TableCell>
                    <TableCell className="text-right">{c.delivered_count}</TableCell>
                    <TableCell className="text-right">{c.returned_count}</TableCell>
                    <TableCell className="text-right">{c.failed_count}</TableCell>
                    <TableCell>{fmtDate(c.created_at)}</TableCell>
                    <TableCell>
                      {(c.status === 'queued' || c.status === 'processing') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(c.id);
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER: Create View
  // ---------------------------------------------------------------------------

  if (view === 'create') {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Create Bulk Mail Campaign</h1>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Campaign Name */}
              <div className="space-y-2">
                <Label htmlFor="campaign-name">Campaign Name</Label>
                <Input
                  id="campaign-name"
                  placeholder="e.g. January Welcome Letters"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </div>

              {/* Template */}
              <div className="space-y-2">
                <Label>Letter Template</Label>
                <Select
                  value={templateId?.toString() ?? ''}
                  onValueChange={(v) => setTemplateId(parseInt(v, 10))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id.toString()}>
                        {t.template_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target */}
              <div className="space-y-3">
                <Label>Target Recipients</Label>
                <Select
                  value={targetMode}
                  onValueChange={(v) => setTargetMode(v as TargetMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="group">Group</SelectItem>
                    <SelectItem value="program">Program</SelectItem>
                    <SelectItem value="manual">Manual Member IDs</SelectItem>
                  </SelectContent>
                </Select>

                {targetMode === 'group' && (
                  <Select
                    value={groupId?.toString() ?? ''}
                    onValueChange={(v) => setGroupId(parseInt(v, 10))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id.toString()}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {targetMode === 'program' && (
                  <Select
                    value={programId?.toString() ?? ''}
                    onValueChange={(v) => setProgramId(parseInt(v, 10))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a program" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p.id} value={p.id.toString()}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {targetMode === 'manual' && (
                  <Input
                    placeholder="Comma-separated member IDs (e.g. 12, 34, 56)"
                    value={manualMemberIds}
                    onChange={(e) => setManualMemberIds(e.target.value)}
                  />
                )}
              </div>

              {/* Mail Options */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Mail Options</Label>

                <div className="space-y-2">
                  <Label>Mail Type</Label>
                  <Select
                    value={mailType}
                    onValueChange={(v) => setMailType(v as 'usps_first_class' | 'usps_standard')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="usps_first_class">USPS First Class</SelectItem>
                      <SelectItem value="usps_standard">USPS Standard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="color-switch">Color Printing</Label>
                  <Switch id="color-switch" checked={color} onCheckedChange={setColor} />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="ds-switch">Double-Sided</Label>
                  <Switch id="ds-switch" checked={doubleSided} onCheckedChange={setDoubleSided} />
                </div>
              </div>

              {/* Merge Data Defaults */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Default Merge Data</Label>
                  <Button variant="outline" size="sm" onClick={addMergeField}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Field
                  </Button>
                </div>
                {mergeFields.length === 0 && (
                  <p className="text-sm text-gray-500">No default merge data. Add key-value pairs to pre-fill template fields.</p>
                )}
                {mergeFields.map((f, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      placeholder="Key"
                      value={f.key}
                      onChange={(e) => updateMergeField(idx, 'key', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Value"
                      value={f.value}
                      onChange={(e) => updateMergeField(idx, 'value', e.target.value)}
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeMergeField(idx)}>
                      <XCircle className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Submit */}
              <Button className="w-full" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Campaign
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER: Detail View
  // ---------------------------------------------------------------------------

  if (view === 'detail' && selectedCampaign) {
    const pct = progressPct(selectedCampaign);

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{selectedCampaign.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={selectedCampaign.status} />
              <span className="text-sm text-gray-500">Created {fmtDate(selectedCampaign.created_at)}</span>
            </div>
          </div>
          {(selectedCampaign.status === 'queued' || selectedCampaign.status === 'processing') && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => handleCancel(selectedCampaign.id)}
            >
              Cancel Campaign
            </Button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium">{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{selectedCampaign.total_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-600 flex items-center gap-2">
                <Send className="h-4 w-4" />
                Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{selectedCampaign.sent_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Delivered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{selectedCampaign.delivered_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-yellow-600 flex items-center gap-2">
                <RotateCw className="h-4 w-4" />
                Returned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{selectedCampaign.returned_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Failed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{selectedCampaign.failed_count}</p>
            </CardContent>
          </Card>
        </div>

        {/* Individual Sends */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Individual Sends</CardTitle>
          </CardHeader>
          <CardContent>
            {loading || sendsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : campaignSends.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No sends recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignSends.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {SEND_STATUS_ICON[s.status] ?? null}
                          <span className="capitalize text-sm">{s.status}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{s.recipient_name ?? '-'}</TableCell>
                      <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                        {s.recipient_address ?? ([s.recipient_address_line1, s.recipient_city, s.recipient_state, s.recipient_zip].filter(Boolean).join(', ') || '-')}
                      </TableCell>
                      <TableCell>{fmtDate(s.sent_at)}</TableCell>
                      <TableCell>{fmtDate(s.delivered_at)}</TableCell>
                      <TableCell className="text-sm text-red-500 max-w-[200px] truncate">
                        {s.error_message ?? '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback (should not reach)
  return null;
};

export default BulkMailCampaignPage;
